import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { sendOTP as sendOTPApi, normalizePhone } from '../../sendOtp';
import { verifyOtpAndSignIn } from '../../services/verifyOtpAndSignIn';
import { applyOtpVerifyResponse } from '../../utils/otpSignIn';
import { useAuth } from '../../contexts/AuthContext';
import AppIcon from '../../components/AppIcon';
import BackButton from '../../components/BackButton';
import OtpInput from '../../components/OtpInput';

const PHOTOGRAPHER_DASHBOARD = '/photographer-dashboard';

const PhotographerLogin = () => {
  const navigate = useNavigate();
  const {
    user,
    userProfile,
    session,
    sessionReady,
    loading: authLoading,
    authStatus,
    AUTH_STATE,
    waitForAuthenticatedProfile,
  } = useAuth();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [msegatId, setMsegatId] = useState(null);
  const verifyInFlightRef = useRef(false);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // مصور مسجّل بالفعل (مثلاً بعد سباق التوجيه) → لوحة التحكم
  useEffect(() => {
    if (!sessionReady || authLoading) return;
    if (authStatus !== AUTH_STATE.AUTHENTICATED || !session?.access_token || !user?.id) return;
    if (userProfile?.role === 'photographer') {
      navigate(PHOTOGRAPHER_DASHBOARD, { replace: true });
    }
  }, [sessionReady, authLoading, authStatus, session, user, userProfile, navigate, AUTH_STATE]);

  const sendOTP = async () => {
    const normalized = normalizePhone(phone);
    if (!normalized) {
      setError('يرجى إدخال رقم الجوال صحيح');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data: existingProfile } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('phone', normalized)
        .maybeSingle();

      if (existingProfile?.role === 'customer') {
        setError('هذا الرقم مسجل كحساب عميل. يرجى استخدام تسجيل دخول العميل.');
        setLoading(false);
        return;
      }
      if (existingProfile?.role === 'admin') {
        setError('هذا الرقم مسجل كحساب مشرف. يرجى استخدام لوحة المشرف.');
        setLoading(false);
        return;
      }
      if (!existingProfile || existingProfile.role !== 'photographer') {
        setError('هذا الرقم غير مسجل كمصور. يرجى إنشاء حساب جديد.');
        setLoading(false);
        return;
      }

      const data = await sendOTPApi(normalized);
      if (!data?.success) {
        throw new Error(data?.error || 'فشل إرسال الرمز');
      }
      setMsegatId(data.id);
      setOtpCode('');
      setStep(2);
      setCountdown(60);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'فشل إرسال كود التحقق');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = useCallback(async (codeOverride) => {
    const code = codeOverride ?? otpCode;
    if (code.length !== 4) {
      setError('يرجى إدخال الكود كاملاً');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const normalized = normalizePhone(phone);
      const data = await verifyOtpAndSignIn({ phone: normalized, code, msegatId });
      await applyOtpVerifyResponse(data);
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('native_role_choice_completed', 'true');
      }
      await waitForAuthenticatedProfile({ role: 'photographer' });
      navigate(PHOTOGRAPHER_DASHBOARD, { replace: true });
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'كود التحقق غير صحيح');
    } finally {
      setLoading(false);
    }
  }, [otpCode, phone, msegatId, waitForAuthenticatedProfile, navigate]);

  const handleOtpComplete = useCallback(
    (code) => {
      if (loading || verifyInFlightRef.current) return;
      verifyInFlightRef.current = true;
      verifyOTP(code).finally(() => {
        verifyInFlightRef.current = false;
      });
    },
    [loading, verifyOTP]
  );

  const resendOTP = () => {
    if (countdown > 0) return;
    setOtpCode('');
    sendOTP();
  };

  const normalizedPhone = normalizePhone(phone);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex flex-col items-center px-4 py-8" dir="rtl">
      <div className="w-full max-w-md flex flex-col items-start mb-4 self-start">
        <BackButton to="/" label="الرئيسية" className="text-white hover:text-white/90" />
      </div>
      <div className="w-full max-w-md flex-1 flex items-center justify-center py-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-2xl mb-4 shadow-lg border-2 border-primary/20">
              <AppIcon name="Camera" size={40} className="text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">
              {step === 1 ? 'دخول المصور' : 'كود التحقق'}
            </h1>
            <p className="text-slate-500 mt-2">
              {step === 1
                ? 'أدخل رقم الجوال لإرسال كود الدخول'
                : `تم إرسال الكود إلى ${normalizedPhone || phone}`}
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-red-600 text-center">{error}</p>
            </div>
          )}

          {step === 1 ? (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  رقم الجوال
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="05xxxxxxxx"
                    className="w-full px-4 py-4 pr-12 rounded-xl border-2 border-slate-200 focus:border-primary focus:outline-none text-left transition-colors"
                    dir="ltr"
                  />
                  <AppIcon name="Phone" size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>
              </div>

              <button
                onClick={sendOTP}
                disabled={loading || !normalizedPhone}
                className="w-full py-4 bg-primary text-primary-foreground font-bold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <AppIcon name="Loader" size={20} className="animate-spin" />
                ) : (
                  <>
                    <AppIcon name="Send" size={20} />
                    إرسال كود التحقق
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-4 text-center">
                  أدخل كود التحقق المكون من 4 أرقام
                </label>
                <OtpInput
                  value={otpCode}
                  onChange={setOtpCode}
                  onComplete={handleOtpComplete}
                  disabled={loading}
                  autoFocus
                />
              </div>

              <button
                onClick={verifyOTP}
                disabled={loading || otpCode.length !== 4}
                className="w-full py-4 bg-primary text-primary-foreground font-bold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <AppIcon name="Loader" size={20} className="animate-spin" />
                ) : (
                  <>
                    <AppIcon name="Check" size={20} />
                    تأكيد
                  </>
                )}
              </button>

              <div className="text-center">
                {countdown > 0 ? (
                  <p className="text-slate-500">
                    إعادة الإرسال بعد <span className="font-bold text-primary">{countdown}</span> ثانية
                  </p>
                ) : (
                  <button
                    onClick={resendOTP}
                    className="text-primary font-bold hover:underline"
                  >
                    إعادة إرسال الكود
                  </button>
                )}
              </div>

              <button
                onClick={() => { setStep(1); setOtpCode(''); setError(''); }}
                className="w-full py-3 text-slate-600 font-medium hover:bg-slate-50 rounded-xl transition-colors"
              >
                تغيير رقم الجوال
              </button>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-slate-100 text-center space-y-3">
            <p className="text-slate-600">
              ليس لديك حساب؟{' '}
              <Link to="/photographer-register" className="text-primary font-bold hover:underline">
                انضم كمصور
              </Link>
            </p>
            <p className="text-slate-500 text-sm">
              هل أنت عميل؟{' '}
              <Link to="/customer-login" className="text-primary font-semibold hover:underline">
                تسجيل دخول العميل
              </Link>
            </p>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2 text-slate-400">
            <AppIcon name="Shield" size={16} />
            <p className="text-xs">تسجيل دخول آمن</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotographerLogin;
